/*
 * <sv-deck>: reusable center-snap video deck used by the product page
 * social proof block. Ports the social-videos section behavior: graded
 * side-card scaling, infinite loop via cloned sets, arrow navigation with
 * manual animation (iOS snap disabled during programmatic scrolls),
 * autoplay-or-preview modes, click-to-play with per-card mute buttons, and
 * IntersectionObserver gating so nothing plays or downloads off screen.
 * Attributes: side-scale="84", autoplay (present = full autoplay),
 * preview="3" (seconds, used when autoplay absent).
 */
(function () {
  if (window.customElements && customElements.get('sv-deck')) return;

  class SVDeck extends HTMLElement {
    connectedCallback() {
      if (this.__init) return;
      this.__init = true;
      var self = this;
      requestAnimationFrame(function () { self._setup(); });
    }

    _setup() {
      var track = this;
      var SIDE_SCALE = parseFloat(track.getAttribute('side-scale') || '100') / 100;
      var AUTOPLAY_FULL = track.hasAttribute('autoplay');
      var PREVIEW_SECONDS = parseFloat(track.getAttribute('preview') || '3');

      var originals = Array.prototype.slice.call(track.querySelectorAll('[data-deck-card]'));
      if (!originals.length) return;
      var SET = originals.length;
      var looping = SET > 1;

      if (looping) {
        var before = document.createDocumentFragment();
        var after = document.createDocumentFragment();
        originals.forEach(function (card) {
          var a = card.cloneNode(true);
          var b = card.cloneNode(true);
          before.appendChild(a);
          after.appendChild(b);
        });
        track.insertBefore(before, originals[0]);
        track.appendChild(after);
      }

      var cards = Array.prototype.slice.call(track.querySelectorAll('[data-deck-card]'));
      var activeIndex = 0;
      var animationToken = 0;
      var targetIndex = null;
      var userVideo = null;
      var inView = false;

      function setMuted(card, video, muted) {
        video.muted = muted;
        var btn = card && card.querySelector('[data-deck-mute]');
        if (btn) {
          btn.classList.toggle('is-unmuted', !muted);
          btn.setAttribute('aria-label', muted ? 'Unmute' : 'Mute');
        }
      }

      cards.forEach(function (card) {
        var video = card.querySelector('video');
        if (!video) return;

        video.addEventListener('play', function () {
          if (AUTOPLAY_FULL || video === userVideo) card.classList.add('is-playing');
        });
        video.addEventListener('pause', function () { card.classList.remove('is-playing'); });
        video.addEventListener('timeupdate', function () {
          if (!AUTOPLAY_FULL && video !== userVideo && video.currentTime >= PREVIEW_SECONDS) {
            video.currentTime = 0;
          }
        });

        card.style.cursor = 'pointer';
        card.addEventListener('click', function () {
          if (AUTOPLAY_FULL) {
            // Autoplay mode: click is pause/resume, never a restart.
            if (video.paused) {
              var rp = video.play();
              if (rp && rp.catch) rp.catch(function () {});
            } else {
              video.pause();
            }
            return;
          }
          var targetCard = card;
          var targetVideo = video;
          var i = cards.indexOf(card);
          if (looping && (i < SET || i >= SET * 2)) {
            // Clicked a clone: teleport to its middle-set copy first, so the
            // post-animation loop normalization never swaps the playing card
            // out from under the user (which muted and reset it).
            var shift = cards[SET * 2].offsetLeft - cards[SET].offsetLeft;
            animationToken++;
            track.scrollLeft += i < SET ? shift : -shift;
            i = SET + (i % SET);
            targetCard = cards[i];
            targetVideo = targetCard.querySelector('video') || video;
          }
          if (userVideo === targetVideo) {
            targetVideo.pause();
            setMuted(targetCard, targetVideo, true);
            userVideo = null;
            return;
          }
          if (userVideo) {
            userVideo.pause();
            setMuted(userVideo.closest('[data-deck-card]'), userVideo, true);
            userVideo = null;
          }
          userVideo = targetVideo;
          if (i !== activeIndex) centerOn(i, false);
          if (targetVideo.readyState === 0) {
            try { targetVideo.load(); } catch (e) {}
            targetVideo.addEventListener('loadedmetadata', function () {
              if (targetVideo === userVideo && targetVideo.paused) {
                var pr = targetVideo.play();
                if (pr && pr.catch) pr.catch(function () {});
              }
            }, { once: true });
          }
          targetVideo.currentTime = 0;
          targetCard.classList.add('is-playing');
          var p = targetVideo.play();
          if (p && p.catch) p.catch(function () {});
        });

        var muteBtn = card.querySelector('[data-deck-mute]');
        if (muteBtn) {
          muteBtn.addEventListener('click', function (event) {
            event.stopPropagation();
            event.preventDefault();
            var willMute = !video.muted;
            setMuted(card, video, willMute);
            // Unmuting the centered preview promotes it to full playback,
            // otherwise the preview loop keeps resetting audible video.
            if (!willMute && !AUTOPLAY_FULL && userVideo !== video && !video.paused) {
              if (userVideo) {
                userVideo.pause();
                setMuted(userVideo.closest('[data-deck-card]'), userVideo, true);
              }
              userVideo = video;
              card.classList.add('is-playing');
            }
          });
        }
      });

      function cancelAnimation() {
        animationToken++;
        targetIndex = null;
        track.style.scrollSnapType = '';
      }

      function centerOn(index, instant) {
        index = Math.max(0, Math.min(cards.length - 1, index));
        var card = cards[index];
        var target = card.offsetLeft - (track.clientWidth - card.offsetWidth) / 2;
        track.style.scrollSnapType = 'none';

        if (instant) {
          animationToken++;
          targetIndex = null;
          track.scrollLeft = target;
          return;
        }

        var from = track.scrollLeft;
        var change = target - from;
        var duration = 350;
        var startTime;
        var token = ++animationToken;
        targetIndex = index;

        function step(timestamp) {
          if (token !== animationToken) return;
          if (startTime === undefined) startTime = timestamp;
          var progress = Math.min((timestamp - startTime) / duration, 1);
          progress = 1 - Math.pow(1 - progress, 3);
          track.scrollLeft = from + change * progress;
          if (progress < 1) {
            requestAnimationFrame(step);
          } else {
            targetIndex = null;
            normalizeLoop();
            syncMedia();
          }
        }

        requestAnimationFrame(step);
      }

      function normalizeLoop() {
        if (!looping || targetIndex !== null) return;
        var shift = cards[SET * 2].offsetLeft - cards[SET].offsetLeft;
        if (activeIndex < SET) {
          animationToken++;
          track.scrollLeft += shift;
        } else if (activeIndex >= SET * 2) {
          animationToken++;
          track.scrollLeft -= shift;
        } else {
          return;
        }
        // Remap activeIndex to the twin right away, otherwise arrow taps and
        // media sync read the pre-teleport index and jump a full set.
        update();
      }

      function goToRelative(direction) {
        normalizeLoop();
        var base = targetIndex !== null ? targetIndex : activeIndex;
        centerOn(base + direction, false);
      }

      ['touchstart', 'wheel'].forEach(function (eventName) {
        track.addEventListener(eventName, cancelAnimation, { passive: true });
      });

      var deckId = track.id;
      if (deckId && window.CSS && CSS.escape) deckId = CSS.escape(deckId);
      if (deckId) {
        document.querySelectorAll('[data-deck-prev][aria-controls="' + deckId + '"]').forEach(function (b) {
          b.addEventListener('click', function () { goToRelative(-1); });
        });
        document.querySelectorAll('[data-deck-next][aria-controls="' + deckId + '"]').forEach(function (b) {
          b.addEventListener('click', function () { goToRelative(1); });
        });
      }

      function update() {
        var rect = track.getBoundingClientRect();
        var center = rect.left + rect.width / 2;
        var best = null;
        var bestDistance = Infinity;
        var isMobile = window.matchMedia('(max-width: 699px)').matches;
        var doGrade = SIDE_SCALE < 1;

        cards.forEach(function (card) {
          var r = card.getBoundingClientRect();
          var d = Math.abs(r.left + r.width / 2 - center);
          if (d < bestDistance) { bestDistance = d; best = card; }

          if (doGrade) {
            var falloff = isMobile ? (r.width || 1) : (r.width || 1) * 2.2;
            var t = Math.min(d / falloff, 1);
            var scale = (1 - (1 - SIDE_SCALE) * t).toFixed(3);
            if (card.__svScale !== scale) {
              card.__svScale = scale;
              card.style.transform = 'scale(' + scale + ')';
            }
          }
          // z-order runs even at side scale 100 so the centered card stays
          // on top of overlapping neighbours.
          var z = String(Math.max(1, 1000 - Math.round(d / 8)));
          if (card.__svZ !== z) {
            card.__svZ = z;
            card.style.zIndex = z;
          }
        });

        activeIndex = cards.indexOf(best);
        cards.forEach(function (card) {
          card.classList.toggle('is-active', card === best);
        });
      }

      function syncMedia() {
        if (!inView) return;
        var active = cards[activeIndex];

        if (userVideo && active && !active.contains(userVideo)) {
          var uCard = userVideo.closest('[data-deck-card]');
          if (uCard) setMuted(uCard, userVideo, true);
          userVideo = null;
        }

        cards.forEach(function (card) {
          var video = card.querySelector('video');
          if (!video || video === userVideo) return;
          if (card === active && video.paused) {
            if (video.readyState === 0) {
              try { video.load(); } catch (e) {}
              video.addEventListener('loadedmetadata', function () {
                if (video.paused) {
                  var pr = video.play();
                  if (pr && pr.catch) pr.catch(function () {});
                }
              }, { once: true });
            }
            var p = video.play();
            if (p && p.catch) p.catch(function () {});
          } else if (card !== active && !video.paused) {
            video.pause();
          }
        });
      }

      var scheduled = false;
      var idleTimer;
      track.addEventListener('scroll', function () {
        clearTimeout(idleTimer);
        idleTimer = setTimeout(function () {
          normalizeLoop();
          syncMedia();
        }, 140);
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(function () { scheduled = false; update(); });
      });

      // Start on the first media (first card of the middle set when looping).
      var first = cards[looping ? SET : 0];
      requestAnimationFrame(function () {
        track.scrollLeft = first.offsetLeft - (track.clientWidth - first.offsetWidth) / 2;
        update();
      });

      var resizeTimer;
      this.__onResize = function () {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () {
          if (targetIndex !== null) return;
          var c = cards[activeIndex] || first;
          track.scrollLeft = c.offsetLeft - (track.clientWidth - c.offsetWidth) / 2;
          update();
        }, 150);
      };
      window.addEventListener('resize', this.__onResize);

      if ('IntersectionObserver' in window) {
        this.__io = new IntersectionObserver(function (entries) {
          inView = entries[0].isIntersecting;
          if (inView) {
            syncMedia();
          } else {
            cards.forEach(function (card) {
              var video = card.querySelector('video');
              if (video && !video.paused) video.pause();
            });
          }
        }, { threshold: 0.15 });
        this.__io.observe(track);
      } else {
        inView = true;
        syncMedia();
      }
    }

    disconnectedCallback() {
      if (this.__io) {
        this.__io.disconnect();
        this.__io = null;
      }
      if (this.__onResize) {
        window.removeEventListener('resize', this.__onResize);
        this.__onResize = null;
      }
    }
  }

  if (window.customElements) customElements.define('sv-deck', SVDeck);
})();
